import { DateTime } from "luxon";
import { AppointmentStatus } from "../types/appointments/AppointmentStatus";
import {
  AppointmentFilter,
  AppointmentQueryNode,
  BooleanNode
} from "../types/reports/appointmentQuery";

export class AppointmentQueryParserError extends Error {
  position: number;

  constructor(message: string, position: number) {
    super(message);
    this.name = "AppointmentQueryParserError";
    this.position = position;
  }
}

type TokenType =
  | "identifier"
  | "number"
  | "string"
  | "comma"
  | "lparen"
  | "rparen"
  | "and"
  | "or";

interface Token {
  type: TokenType;
  value?: string | number;
  position: number;
}

const DAY_NAME_MAP: Record<string, number> = {
  sunday: 1,
  monday: 2,
  tuesday: 3,
  wednesday: 4,
  thursday: 5,
  friday: 6,
  saturday: 7,
};

export function parseAppointmentQuery(text: string): AppointmentQueryNode {
  const tokens = tokenize(text);
  const parser = new Parser(tokens);
  const node = parser.parseExpression();
  parser.expectEnd();
  return node;
}

class Parser {
  private index = 0;

  constructor(private readonly tokens: Token[]) { }

  parseExpression(): AppointmentQueryNode {
    let left = this.parseTerm();
    while (this.match("and")) {
      const operatorToken = this.previous();
      const right = this.parseTerm();
      left = combineBooleanNodes("and", left, right, operatorToken.position);
    }
    return left;
  }

  parseTerm(): AppointmentQueryNode {
    let left = this.parseFactor();
    while (this.match("or")) {
      const operatorToken = this.previous();
      const right = this.parseFactor();
      left = combineBooleanNodes("or", left, right, operatorToken.position);
    }
    return left;
  }

  parseFactor(): AppointmentQueryNode {
    if (this.match("lparen")) {
      const expr = this.parseExpression();
      this.expect("rparen");
      return expr;
    }
    return this.parseFunction();
  }

  parseFunction(): AppointmentQueryNode {
    const identifier = this.expect("identifier");
    this.expect("lparen");
    const args: (string | number)[] = [];
    if (!this.check("rparen")) {
      do {
        args.push(this.parseArgument());
      } while (this.match("comma"));
    }
    this.expect("rparen");
    const filter = resolveFilter(identifier.value as string, args, identifier.position);
    return {
      type: "filter",
      filter,
    };
  }

  parseArgument(): string | number {
    if (this.match("string")) {
      return this.previous().value as string;
    }
    if (this.match("number")) {
      return this.previous().value as number;
    }
    if (this.match("identifier")) {
      // allow bare identifiers such as RoleFilter(referee, A)
      const token = this.previous();
      const value = token.value as string;
      if (/^-?\d+(\.\d+)?$/.test(value)) {
        return Number(value);
      }
      return value;
    }
    throw this.error("Expected string, number or identifier");
  }

  expect(type: TokenType) {
    if (this.match(type)) {
      return this.previous();
    }
    throw this.error(`Expected ${type}`);
  }

  expectEnd() {
    if (!this.isAtEnd()) {
      throw this.error("Unexpected content after expression");
    }
  }

  match(type: TokenType): boolean {
    if (this.check(type)) {
      this.advance();
      return true;
    }
    return false;
  }

  check(type: TokenType): boolean {
    if (this.isAtEnd()) return false;
    return this.peek().type === type;
  }

  advance(): Token {
    if (!this.isAtEnd()) this.index += 1;
    return this.previous();
  }

  previous(): Token {
    return this.tokens[this.index - 1];
  }

  peek(): Token {
    return this.tokens[this.index];
  }

  isAtEnd(): boolean {
    return this.index >= this.tokens.length;
  }

  error(message: string): never {
    const position = this.isAtEnd() ? (this.tokens[this.tokens.length - 1]?.position ?? 0) : this.peek().position;
    throw new AppointmentQueryParserError(message, position);
  }
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  const push = (token: Token) => tokens.push(token);

  while (index < text.length) {
    const char = text[index];
    if (/\s/.test(char)) {
      index += 1;
      continue;
    }

    if (char === ",") {
      push({ type: "comma", position: index });
      index += 1;
      continue;
    }

    if (char === "(") {
      push({ type: "lparen", position: index });
      index += 1;
      continue;
    }

    if (char === ")") {
      push({ type: "rparen", position: index });
      index += 1;
      continue;
    }

    if (char === '"' || char === "'") {
      const quote = char;
      let value = "";
      index += 1;
      let escaped = false;
      while (index < text.length) {
        const current = text[index];
        if (escaped) {
          value += current;
          escaped = false;
        } else if (current === "\\") {
          escaped = true;
        } else if (current === quote) {
          break;
        } else {
          value += current;
        }
        index += 1;
      }
      if (index >= text.length || text[index] !== quote) {
        throw new AppointmentQueryParserError("Unterminated string literal", index);
      }
      index += 1;
      push({ type: "string", value, position: index - value.length - 2 });
      continue;
    }

    if (isDigit(char) || (char === "-" && isDigit(text[index + 1] || ""))) {
      const start = index;
      index += 1;
      while (index < text.length && (isDigit(text[index]) || text[index] === ".")) {
        index += 1;
      }
      const value = Number(text.slice(start, index));
      if (Number.isNaN(value)) {
        throw new AppointmentQueryParserError("Invalid number literal", start);
      }
      push({ type: "number", value, position: start });
      continue;
    }

    if (isAlpha(char)) {
      const start = index;
      index += 1;
      while (index < text.length && isAlphaNumeric(text[index])) {
        index += 1;
      }
      const raw = text.slice(start, index);
      const upper = raw.toUpperCase();
      if (upper === "AND") {
        push({ type: "and", position: start });
      } else if (upper === "OR") {
        push({ type: "or", position: start });
      } else {
        push({ type: "identifier", value: raw, position: start });
      }
      continue;
    }

    throw new AppointmentQueryParserError(`Unexpected character '${char}'`, index);
  }

  return tokens;
}

function combineBooleanNodes(
  type: BooleanNode["type"],
  left: AppointmentQueryNode,
  right: AppointmentQueryNode,
  position: number,
): AppointmentQueryNode {
  const existingLeft = (left as BooleanNode).type === type ? (left as BooleanNode) : null;
  const existingRight = (right as BooleanNode).type === type ? (right as BooleanNode) : null;

  if (existingLeft && existingRight) {
    return {
      type,
      nodes: [...existingLeft.nodes, ...existingRight.nodes],
    };
  }
  if (existingLeft) {
    return {
      type,
      nodes: [...existingLeft.nodes, right],
    };
  }
  if (existingRight) {
    return {
      type,
      nodes: [left, ...existingRight.nodes],
    };
  }
  return {
    type,
    nodes: [left, right],
  };
}

function resolveFilter(name: string, args: (string | number)[], position: number): AppointmentFilter {
  const fn = name.toLowerCase();
  switch (fn) {
    case "daterange":
      if (args.length === 0 || args.length > 2) {
        throw new AppointmentQueryParserError("DateRange requires one or two arguments", position);
      }
      return {
        kind: "dateRange",
        from: toIsoDate(args[0]),
        to: args[1] != null ? toIsoDate(args[1]) : undefined,
      };
    case "day":
    case "days":
      if (args.length === 0) {
        throw new AppointmentQueryParserError("Day requires at least one argument", position);
      }
      return {
        kind: "dayOfWeek",
        days: args.map(toDayOfWeek),
      };
    case "rolefilter":
      if (args.length === 0) {
        throw new AppointmentQueryParserError("RoleFilter requires a role", position);
      }
      const grades =
        args.length > 1 ? args.slice(1).map((arg) => normaliseGradeArg(arg, position)) : undefined;
      return {
        kind: "role",
        role: String(args[0]),
        grades,
      };
    case "position":
      if (args.length === 0) {
        throw new AppointmentQueryParserError("Position requires at least one value", position);
      }
      return {
        kind: "position",
        values: args.map((a) => String(a)),
      };
    case "person":
    case "persons":
    case "personfilter":
      if (args.length === 0) {
        throw new AppointmentQueryParserError("Person requires at least one id", position);
      }
      return {
        kind: "person",
        ids: args.map((arg) => String(arg)),
      };
    case "shift":
      if (args.length === 0) {
        throw new AppointmentQueryParserError("Shift requires at least one id", position);
      }
      return {
        kind: "shift",
        ids: args.map((arg) => String(arg)),
      };
    case "tenure":
      if (args.length === 0) {
        throw new AppointmentQueryParserError("Tenure requires at least one value", position);
      }
      return {
        kind: "tenure",
        values: args.map((arg) => String(arg)),
      };
    case "appointmenttype":
      if (args.length === 0) {
        throw new AppointmentQueryParserError("AppointmentType requires at least one value", position);
      }
      return {
        kind: "appointmentType",
        types: args.map((arg) => String(arg).toLowerCase()),
      };
    case "stillappointed":
      if (args.length !== 1) {
        throw new AppointmentQueryParserError("StillAppointed requires a single value", position);
      }
      return {
        kind: "stillAppointed",
        value: parseBooleanArg(args[0], position),
      };
    case "appointmentstatus":
      if (args.length === 0) {
        throw new AppointmentQueryParserError("AppointmentStatus requires at least one value", position);
      }
      return {
        kind: "appointmentStatus",
        statuses: args.map((arg) => normaliseStatusArg(arg, position)),
      };
    default:
      throw new AppointmentQueryParserError(`Unknown function ${name}`, position);
  }
}

function toIsoDate(value: string | number): string {
  if (typeof value === "number") {
    return DateTime.fromMillis(value).toISODate();
  }
  const text = String(value);
  const parsed = DateTime.fromISO(text);
  if (!parsed.isValid) {
    throw new AppointmentQueryParserError(`Invalid ISO date '${text}'`, 0);
  }
  return parsed.toISODate();
}

function toNumber(value: string | number, context: string): number {
  if (typeof value === "number") return value;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    throw new AppointmentQueryParserError(`${context} must be numeric`, 0);
  }
  return parsed;
}

function toDayOfWeek(value: string | number): number {
  if (typeof value === "number") {
    if (value < 1 || value > 7) {
      throw new AppointmentQueryParserError("Day of week must be between 1 and 7", 0);
    }
    return value;
  }
  const key = value.toLowerCase();
  const day = DAY_NAME_MAP[key];
  if (!day) {
    throw new AppointmentQueryParserError(`Unknown day name '${value}'`, 0);
  }
  return day;
}

function normaliseGradeArg(value: string | number, position: number): number | string {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  const text = value.toString().trim();
  if (text.length === 0) {
    throw new AppointmentQueryParserError("Role grade cannot be empty", position);
  }
  const numeric = Number(text);
  if (!Number.isNaN(numeric)) {
    return numeric;
  }
  return text;
}

function parseBooleanArg(value: string | number, position: number): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  const text = value.toString().trim().toLowerCase();
  if (text === "true") return true;
  if (text === "false") return false;
  throw new AppointmentQueryParserError("Boolean argument must be true or false", position);
}

function normaliseStatusArg(value: string | number, position: number): string {
  if (value == null) {
    throw new AppointmentQueryParserError("AppointmentStatus values cannot be empty", position);
  }
  if (typeof value === "number" && !Number.isNaN(value)) {
    const key = AppointmentStatus[value as keyof typeof AppointmentStatus];
    if (typeof key === "string") {
      return key;
    }
    return value.toString();
  }
  const text = value.toString().trim();
  if (text.length === 0) {
    throw new AppointmentQueryParserError("AppointmentStatus values cannot be empty", position);
  }
  const numeric = Number(text);
  if (!Number.isNaN(numeric)) {
    const key = AppointmentStatus[numeric as keyof typeof AppointmentStatus];
    if (typeof key === "string") {
      return key;
    }
    return numeric.toString();
  }
  const matchKey = Object.keys(AppointmentStatus)
    .filter((key) => isNaN(Number(key)))
    .find((key) => key.toLowerCase() === text.toLowerCase());
  if (matchKey) {
    return matchKey;
  }
  return text;
}

function isDigit(char: string): boolean {
  return char >= "0" && char <= "9";
}

function isAlpha(char: string): boolean {
  return /[a-z]/i.test(char);
}

function isAlphaNumeric(char: string): boolean {
  return /[a-z0-9]/i.test(char);
}
